import { Cloudinary } from 'cloudinary-core';
import gql from 'graphql-tag';
import { computed, ref, watch } from 'vue';
import apolloClient from '@/apollo/client';
import config from '@/config';
import { handledException } from '@/helpers/datadog';
import { isCdnSource } from './cdn-source';

type GalleryImage = {
  id: string;
  url: string;
};

const imageTransformUrlsQuery = gql`
  query imageTransformUrls($inputs: [ImageTransformInput!]!) {
    imageTransformUrls(inputs: $inputs)
  }
`;

const thumbnail = { width: 400, height: 400, crop: 'fill' };
const maxInputs = 50;
// Avoid downloading full originals while thumbnails are pending or requests fail.
const placeholder =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export const useGalleryImages = (
  sources: () => string[],
  update: (sources: string[]) => void,
) => {
  const cloudinaryCore = config.cloudinaryCloudName
    ? new Cloudinary({ cloud_name: config.cloudinaryCloudName })
    : null;

  const signedUrls = ref<Record<string, string>>({});
  const requested = new Set<string>();

  const fetchSignedUrls = async (values: string[]) => {
    const pending = values.filter(
      (source) => isCdnSource(source) && !requested.has(source),
    );

    if (!pending.length) {
      return;
    }

    pending.forEach((source) => {
      requested.add(source);
    });

    for (let index = 0; index < pending.length; index += maxInputs) {
      const batch = pending.slice(index, index + maxInputs);

      try {
        const { data } = await apolloClient.query<{
          imageTransformUrls: (string | null)[];
        }>({
          query: imageTransformUrlsQuery,
          variables: {
            inputs: batch.map((source) => ({ source, ...thumbnail })),
          },
          fetchPolicy: 'no-cache',
        });

        const resolved: Record<string, string> = {};

        // Null means legacy passthrough, not a failed signing request.
        data?.imageTransformUrls?.forEach((url, position) => {
          resolved[batch[position]] = url || batch[position];
        });
        signedUrls.value = { ...signedUrls.value, ...resolved };
      } catch (error) {
        batch.forEach((source) => {
          requested.delete(source);
        });
        handledException(error, { operationName: 'imageTransformUrls' });
      }
    }
  };

  watch(sources, fetchSignedUrls, { immediate: true });

  const getImageUrl = (source: string) => {
    if (source.startsWith('blob:')) {
      return source;
    }

    if (isCdnSource(source)) {
      return signedUrls.value[source] || placeholder;
    }

    return cloudinaryCore?.url(source, thumbnail) || source;
  };

  return computed<GalleryImage[]>({
    get: () =>
      sources().map((source) => ({
        id: source,
        url: getImageUrl(source),
      })),
    set: (images) => {
      update(images.map((image) => image.id));
    },
  });
};
